<template>
  <div class="iso-templates-page">
    <!-- ===================== Hero ===================== -->
    <div class="iso-hero">
      <div class="iso-hero__blob iso-hero__blob--one" />
      <div class="iso-hero__blob iso-hero__blob--two" />

      <div class="iso-hero__content">
        <div class="iso-hero__icon">
          <feather-icon
            icon="AwardIcon"
            size="30"
          />
        </div>
        <div>
          <h2 class="iso-hero__title">
            ISO Templates
          </h2>
          <p class="iso-hero__subtitle">
            Browse ISO certificate templates and the engineers assigned to each one.
          </p>
        </div>
      </div>
    </div>

    <!-- ===================== Toolbar ===================== -->
    <b-card
      no-body
      class="iso-toolbar-card mb-1"
    >
      <div class="iso-toolbar">
        <div class="iso-toolbar__title">
          <b-avatar
            rounded
            size="40"
            class="mr-1 iso-toolbar__avatar"
          >
            <feather-icon
              icon="LayersIcon"
              size="20"
            />
          </b-avatar>
          <div>
            <h4 class="mb-0 font-weight-bolder">
              Certificate Templates
            </h4>
            <small class="text-muted">ISO source certificates and their engineers</small>
          </div>
        </div>

        <b-input-group class="iso-search">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery"
            placeholder="Search templates or engineers..."
            autocomplete="off"
          />
          <b-input-group-append
            v-if="searchQuery"
            is-text
            class="iso-search__clear"
            @click="searchQuery = ''"
          >
            <feather-icon icon="XIcon" />
          </b-input-group-append>
        </b-input-group>
      </div>
    </b-card>

    <!-- ===================== Loading ===================== -->
    <b-row v-if="dataLoad">
      <b-col
        v-for="n in 6"
        :key="n"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="iso-skel">
          <div class="iso-skel__accent" />
          <div class="iso-skel__head">
            <div class="iso-skel__avatar" />
            <div class="iso-skel__lines">
              <div class="iso-skel__line iso-skel__line--short" />
              <div class="iso-skel__line iso-skel__line--long" />
            </div>
          </div>
          <div class="iso-skel__body">
            <div class="iso-skel__chip" />
            <div class="iso-skel__chip" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Empty ===================== -->
    <b-card
      v-else-if="!filteredRows.length"
      class="text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-1"
      />
      <h4>No ISO templates found</h4>
      <p class="text-muted mb-0">
        {{ searchQuery ? 'Try adjusting your search.' : 'There are no templates to display yet.' }}
      </p>
    </b-card>

    <!-- ===================== Cards ===================== -->
    <b-row
      v-else
      class="iso-grid"
    >
      <b-col
        v-for="row in filteredRows"
        :key="row.id"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="iso-card">
          <div
            class="iso-card__accent"
            :style="{ background: getGradient(row) }"
          />

          <div class="iso-card__head">
            <div
              class="iso-card__avatar"
              :style="{ background: getGradient(row) }"
            >
              <feather-icon
                :icon="getIcon(row)"
                size="22"
              />
            </div>
            <div class="iso-card__head-text">
              <h4
                class="iso-card__name"
                :title="getName(row)"
              >
                {{ getName(row) }}
              </h4>
              <span class="iso-card__id">#{{ row.id }}</span>
            </div>
            <span class="iso-card__source">
              <feather-icon
                icon="ShieldIcon"
                size="12"
                class="mr-25"
              />
              {{ row.source || 'ISO' }}
            </span>
          </div>

          <div class="iso-card__body">
            <div class="iso-card__section-label">
              <feather-icon
                icon="UsersIcon"
                size="14"
                class="mr-25"
              />
              Assigned Engineers
              <b-badge
                pill
                class="ml-50 iso-card__badge"
              >
                {{ engineerList(row).length }}
              </b-badge>
            </div>

            <div
              v-if="engineerList(row).length"
              class="iso-engineers"
            >
              <span
                v-for="(eng, idx) in previewEngineers(row)"
                :key="`${row.id}-${idx}`"
                class="iso-engineer-chip"
              >
                <span
                  class="iso-engineer-chip__dot"
                  :style="{ background: getGradient(row) }"
                >
                  {{ eng.charAt(0).toUpperCase() }}
                </span>
                {{ eng }}
              </span>

              <button
                v-if="engineerList(row).length > engineerPreviewLimit"
                type="button"
                class="iso-engineer-chip iso-engineer-chip--more"
                @click="openEngineers(row)"
              >
                +{{ engineerList(row).length - engineerPreviewLimit }} more
              </button>
            </div>
            <span
              v-else
              class="iso-empty-hint"
            >
              No engineers assigned
            </span>
          </div>

          <div class="iso-card__footer">
            <button
              type="button"
              class="iso-edit-btn"
              @click="goToEdit(row)"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                class="mr-25"
              />
              Template
            </button>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Pagination ===================== -->
    <div
      v-if="!dataLoad && isoTemplates.length"
      class="mt-1"
    >
      <Pagination
        :page="page"
        :per-page="perPage"
        :total-rows="totalRows"
        @get-all-data="fetchIsoTemplates"
      />
    </div>

    <!-- ===================== Engineers modal ===================== -->
    <b-modal
      id="iso-engineers-modal"
      v-model="engineersModal"
      centered
      hide-footer
      size="md"
      modal-class="iso-eng-modal"
    >
      <template #modal-title>
        <div class="iso-eng-modal__title">
          <span
            class="iso-eng-modal__icon"
            :style="{ background: activeTemplate ? getGradient(activeTemplate) : '' }"
          >
            <feather-icon
              icon="UsersIcon"
              size="16"
            />
          </span>
          <div>
            <div class="iso-eng-modal__name">
              {{ activeTemplate ? getName(activeTemplate) : '' }}
            </div>
            <small class="text-muted">{{ activeEngineers.length }} engineers</small>
          </div>
        </div>
      </template>

      <div class="iso-eng-modal__search">
        <b-input-group>
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="engineerSearch"
            placeholder="Filter engineers..."
            autocomplete="off"
          />
          <b-input-group-append
            v-if="engineerSearch"
            is-text
            class="iso-search__clear"
            @click="engineerSearch = ''"
          >
            <feather-icon icon="XIcon" />
          </b-input-group-append>
        </b-input-group>
      </div>

      <div
        v-if="activeEngineers.length"
        class="iso-eng-modal__list"
      >
        <div
          v-for="(eng, index) in activeEngineers"
          :key="`${index}-${eng}`"
          class="iso-eng-modal__item"
        >
          <span
            class="iso-eng-modal__avatar"
            :style="{ background: activeTemplate ? getGradient(activeTemplate) : '' }"
          >
            {{ eng.charAt(0).toUpperCase() }}
          </span>
          <span class="iso-eng-modal__text">{{ eng }}</span>
        </div>
      </div>
      <div
        v-else
        class="text-center text-muted py-3"
      >
        No engineers match your filter.
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BRow, BCol, BCard, BAvatar, BBadge, BModal,
  BInputGroup, BInputGroupPrepend, BInputGroupAppend, BFormInput,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import Pagination from '@/components/general-table/specific-utils/Pagination.vue'

const ISO_ICON_RULES = [
  { keys: ['9001'], icon: 'CheckCircleIcon' },
  { keys: ['22000', 'food'], icon: 'CoffeeIcon' },
  { keys: ['14001', 'environ'], icon: 'CloudIcon' },
  { keys: ['45001', 'safety', 'ohsas'], icon: 'ShieldIcon' },
  { keys: ['27001', 'security'], icon: 'LockIcon' },
  { keys: ['50001', 'energy'], icon: 'ZapIcon' },
]

const ISO_ICON_FALLBACK = ['AwardIcon', 'FileTextIcon', 'BookmarkIcon', 'LayersIcon']

// Harmonious teal / cyan / emerald palette
const ISO_GRADIENTS = [
  'linear-gradient(135deg, #0d9488, #2dd4bf)',
  'linear-gradient(135deg, #0891b2, #22d3ee)',
  'linear-gradient(135deg, #059669, #34d399)',
  'linear-gradient(135deg, #0e7490, #67e8f9)',
  'linear-gradient(135deg, #14b8a6, #5eead4)',
  'linear-gradient(135deg, #0284c7, #38bdf8)',
]

export default {
  components: {
    Pagination,
    BRow,
    BCol,
    BCard,
    BAvatar,
    BBadge,
    BModal,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormInput,
  },
  data() {
    return {
      isoTemplates: [],
      dataLoad: false,
      page: 1,
      perPage: 10,
      totalRows: 0,
      searchQuery: '',
      engineerPreviewLimit: 2,
      engineersModal: false,
      engineerSearch: '',
      activeTemplate: null,
    }
  },
  computed: {
    filteredRows() {
      const term = this.searchQuery.trim().toLowerCase()
      if (!term) return this.isoTemplates

      return this.isoTemplates.filter(row => {
        const name = this.getName(row).toLowerCase()
        const engineers = (row.engineer_names || '').toLowerCase()
        const source = (row.source || '').toLowerCase()
        return name.includes(term) || engineers.includes(term) || source.includes(term)
      })
    },
    activeEngineers() {
      const engineers = this.engineerList(this.activeTemplate)
      const term = this.engineerSearch.trim().toLowerCase()
      if (!term) return engineers
      return engineers.filter(name => name.toLowerCase().includes(term))
    },
  },
  mounted() {
    this.fetchIsoTemplates()
  },
  methods: {
    async fetchIsoTemplates(page = 1) {
      this.dataLoad = true
      this.page = page

      try {
        const skip = (page - 1) * this.perPage || 0
        const params = { skip, appInterface: 'ISO' }
        const response = await axios.get('certificate_group', { params })

        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const data = responseData?.data || {}
          this.isoTemplates = data.certificates || []
          this.totalRows = data.count || this.isoTemplates.length || 0
        }
      } catch (error) {
        if (error.response?.status === 500) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Server Error',
              icon: 'BellIcon',
              variant: 'danger',
            },
          })
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Failed to load ISO templates',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      } finally {
        this.dataLoad = false
      }
    },

    getName(row) {
      if (!row || !row.name) return 'Untitled template'
      return row.name.en || row.name.ar || 'Untitled template'
    },

    engineerList(row) {
      if (!row || !row.engineer_names) return []
      return String(row.engineer_names)
        .split(',')
        .map(name => name.trim())
        .filter(Boolean)
    },

    previewEngineers(row) {
      return this.engineerList(row).slice(0, this.engineerPreviewLimit)
    },

    openEngineers(row) {
      this.activeTemplate = row
      this.engineerSearch = ''
      this.engineersModal = true
    },

    getIcon(row) {
      const name = this.getName(row).toLowerCase()
      const rule = ISO_ICON_RULES.find(({ keys }) => keys.some(key => name.includes(key)))
      if (rule) return rule.icon
      return ISO_ICON_FALLBACK[row.id % ISO_ICON_FALLBACK.length]
    },

    getGradient(row) {
      return ISO_GRADIENTS[Math.abs(Number(row.id) || 0) % ISO_GRADIENTS.length]
    },

    goToEdit(row) {
      this.$router.push({
        name: 'edit-iso-template',
        params: { id: row.id },
      })
    },
  },
}
</script>

<style lang="scss" scoped src="../IsoTemplates.scss"></style>
